import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router, RouterStateSnapshot } from '@angular/router';
import { map, take } from 'rxjs';

import { RealtorFacade } from 'src/app/modules/ngrx-store/realtor/realtor.facade';
import { CORE_ROUTES, NAVIGATION_ROUTES } from 'src/app/modules/shared/constants/route-paths';

/**
 * Blocks access to a route when the user is previewing as a realtor.
 * Service Requests and other customer-only pages should not be accessible in realtor preview mode.
 */
export const nonRealtorPreviewGuard: CanMatchFn = () => {
	const router = inject(Router);
	const previewRealtorBase = `/${CORE_ROUTES.Preview}/${NAVIGATION_ROUTES.Realtor}/`;
	const nav = router.currentNavigation();
	const targetUrl = nav?.finalUrl ? router.serializeUrl(nav.finalUrl) : router.url;
	return !targetUrl.startsWith(previewRealtorBase);
};

/**
 * Redirect preview deep links back to the client management page when there are no grants.
 * This prevents the dashboard from loading for preview users with no available preview context.
 */
export const previewRealtorDeepLinkGuard: CanActivateFn = (_route, state: RouterStateSnapshot) => {
	const router = inject(Router);
	const realtorFacade = inject(RealtorFacade);
	const previewRealtorBase = `/${CORE_ROUTES.Preview}/${NAVIGATION_ROUTES.Realtor}`;
	const currentUrl = state.url;

	if (!currentUrl.includes(`/${CORE_ROUTES.Preview}/`)) {
		return true;
	}

	if (currentUrl === previewRealtorBase) {
		return true;
	}

	if (currentUrl.startsWith(`${previewRealtorBase}/`)) {
		return realtorFacade.realtorGrants$.pipe(
			take(1),
			map((grants) => Boolean(grants?.length) || router.parseUrl(previewRealtorBase))
		);
	}

	return router.parseUrl(previewRealtorBase);
};
